import React from 'react';
import {
  Check as CheckIcon,
  Description as DescriptionIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { es as esLocale } from 'date-fns/locale';
import SalesStep1 from '../SalesStep1';
import { SalesStep2 } from '../SalesStep2';
import SalesStep3 from '../SalesStep3';
import SalesStep3Customer from '../SalesStep3Customer/SalesStep3Customer';
import { useSalesModal } from './SalesModal.hooks';
import { STORE_INFO } from '../../../utils/constants';
import { formatCurrency } from '../../../utils/formatters/money';
import { SelectedProduct } from '../../../types/sales.types';
import { CloseConfirmationModal } from '../../modals/confirmation';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import {
  SALES_CUSTOMER_STEP_ENABLED,
  BRANCH_RECEIPT_NUMBERING_ENABLED,
} from '../../../config/feature-flags';
import {
  BRANCH_RECEIPT_NUMBER_LABEL,
  SALES_ORDER_NUMBER_LABEL,
} from '../../../utils/sales/branch-receipt-numbering';
import {
  Backdrop,
  SlidePanel,
  Header,
  HeaderContent,
  HeaderTitle,
  CloseButton,
  ProgressSection,
  ProgressHeader,
  ProgressTitle,
  ProgressLabel,
  ProgressBarContainer,
  ProgressBar,
  StepBody,
  SuccessContainer,
  SuccessContent,
  SuccessIcon,
  SuccessTitle,
  SuccessMessage,
  SuccessOrderNumber,
  SummaryCard,
  SummaryGrid,
  SummaryLabel,
  SummaryValue,
  SummaryStatus,
  ActionsContainer,
  PrimaryButton,
  SecondaryButton,
  PrintModalBackdrop,
  PrintModalContent,
  PrintModalHeader,
  PrintModalTitle,
  PrintModalBody,
  PrintModalFooter,
  Receipt,
  ReceiptHeader,
  ReceiptTitle,
  ReceiptInfo,
  ReceiptDivider,
  ReceiptSection,
  ReceiptRow,
  ReceiptProductRow,
  ReceiptBold,
  ReceiptTotal,
} from './SalesModal.styles';

const SalesModal: React.FC = () => {
  const {
    // State
    isOpen,
    currentStep,
    isProcessing,
    isCompleted,
    orderNumber,
    branchReceiptNumber,
    formattedOrderCompletedAt,
    showPrintModal,
    showCancelConfirmation,
    totalSteps,
    selectedLocation,
    selectedProducts,
    selectedCustomer,
    progressPercentage,
    stepTitle,
    formattedGrandTotal,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    calculateOrderSummary,
    // Handlers
    handleOrderCompleted,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    handleClose,
    handleCloseClick,
    cancelOperation,
    handleCloseConfirmation,
    handlePrintReceipt,
    handlePrint,
    handleFinish,
    handleClosePrintModal,
  } = useSalesModal();

  // Interceptar ESC para mostrar confirmación de cierre
  useEscapeKey(
    () => {
      if (isOpen && !isProcessing && !isCompleted && !showCancelConfirmation) {
        handleCloseClick();
      }
    },
    isOpen && !isProcessing && !isCompleted && !showCancelConfirmation,
  );

  // Render step content
  const renderStepContent = () => {
    if (SALES_CUSTOMER_STEP_ENABLED) {
      switch (currentStep) {
        case 1:
          return <SalesStep1 />;
        case 2:
          return <SalesStep2 />;
        case 3:
          return <SalesStep3Customer />;
        case 4:
          return <SalesStep3 onOrderCompleted={handleOrderCompleted} />;
        default:
          return <SalesStep1 />;
      }
    }

    switch (currentStep) {
      case 1:
        return <SalesStep1 />;
      case 2:
        return <SalesStep2 />;
      case 3:
        return <SalesStep3 onOrderCompleted={handleOrderCompleted} />;
      default:
        return <SalesStep1 />;
    }
  };

  if (!isOpen) return null;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={esLocale}>
      {/* Backdrop */}
      <Backdrop>
        {/* Slide-out Panel */}
        <SlidePanel>
          {/* Header */}
          <Header>
            <HeaderContent>
              <HeaderTitle>Nueva Venta</HeaderTitle>
              <CloseButton onClick={handleCloseClick} disabled={isProcessing}>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="24" height="24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </CloseButton>
            </HeaderContent>
          </Header>

          {/* Progress Bar */}
          <ProgressSection>
            <ProgressHeader>
              <ProgressTitle>{stepTitle}</ProgressTitle>
              <ProgressLabel>
                Paso {currentStep} de {totalSteps}
              </ProgressLabel>
            </ProgressHeader>
            <ProgressBarContainer>
              <ProgressBar progress={progressPercentage} />
            </ProgressBarContainer>
          </ProgressSection>

          {/* Content */}
          {!isCompleted ? (
            <StepBody>{renderStepContent()}</StepBody>
          ) : (
            // Success Screen
            <SuccessContainer>
              <SuccessContent>
                <SuccessIcon>
                  <CheckIcon style={{ color: '#ffffff', fontSize: 40 }} />
                </SuccessIcon>

                <SuccessTitle>¡Venta Confirmada!</SuccessTitle>
                <SuccessMessage>
                  La venta <SuccessOrderNumber>{orderNumber}</SuccessOrderNumber> se registró con
                  éxito.
                </SuccessMessage>

                <SummaryCard>
                  <SummaryGrid>
                    <div>
                      <SummaryLabel>Total:</SummaryLabel>
                      <SummaryValue>{formattedGrandTotal}</SummaryValue>
                    </div>
                    <div>
                      <SummaryLabel>Estado:</SummaryLabel>
                      <SummaryStatus>Pagado</SummaryStatus>
                    </div>
                  </SummaryGrid>
                  {formattedOrderCompletedAt ? (
                    <div style={{ marginTop: '16px', textAlign: 'center' }}>
                      <SummaryLabel>Fecha de la venta</SummaryLabel>
                      <div style={{ fontWeight: 600, color: '#374151', marginTop: '4px' }}>
                        {formattedOrderCompletedAt}
                      </div>
                    </div>
                  ) : null}
                </SummaryCard>

                {/* Action Buttons */}
                <ActionsContainer>
                  <PrimaryButton onClick={handlePrintReceipt}>
                    <DescriptionIcon style={{ fontSize: 16, marginRight: 8 }} />
                    Imprimir Comprobante
                  </PrimaryButton>

                  <SecondaryButton onClick={handleFinish}>Finalizar y Cerrar</SecondaryButton>
                </ActionsContainer>
              </SuccessContent>
            </SuccessContainer>
          )}
        </SlidePanel>
      </Backdrop>

      {/* Print Modal */}
      {showPrintModal && (
        <PrintModalBackdrop>
          <PrintModalContent>
            <PrintModalHeader>
              <PrintModalTitle>Comprobante de Venta</PrintModalTitle>
              <CloseButton onClick={handleClosePrintModal}>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="24" height="24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </CloseButton>
            </PrintModalHeader>

            {/* Receipt Content */}
            <PrintModalBody>
              <Receipt id="receipt-content">
                <ReceiptHeader>
                  <ReceiptTitle>{STORE_INFO.name}</ReceiptTitle>
                  <ReceiptInfo>
                    <div>{STORE_INFO.address}</div>
                    <div>{STORE_INFO.city}</div>
                    <div>Tel: {STORE_INFO.phone}</div>
                    <div>NIT: {STORE_INFO.nit}</div>
                  </ReceiptInfo>
                </ReceiptHeader>

                <ReceiptDivider>
                  <div style={{ textAlign: 'center', fontWeight: 700, marginBottom: '8px' }}>
                    COMPROBANTE DE VENTA
                  </div>
                </ReceiptDivider>

                <ReceiptSection>
                  <ReceiptRow>
                    <span style={{ width: '100%' }}>
                      {SALES_ORDER_NUMBER_LABEL}: {orderNumber}
                    </span>
                  </ReceiptRow>
                  {BRANCH_RECEIPT_NUMBERING_ENABLED && branchReceiptNumber ? (
                    <ReceiptRow>
                      <span style={{ width: '100%' }}>
                        {BRANCH_RECEIPT_NUMBER_LABEL}: {branchReceiptNumber}
                      </span>
                    </ReceiptRow>
                  ) : null}
                  <ReceiptRow>
                    <span style={{ width: '100%' }}>
                      Fecha y hora:{' '}
                      {formattedOrderCompletedAt || new Date().toLocaleString('es-ES')}
                    </span>
                  </ReceiptRow>
                  <ReceiptRow>
                    <span style={{ width: '100%' }}>
                      Sucursal: {selectedLocation?.name || 'N/A'}
                    </span>
                  </ReceiptRow>
                  <ReceiptRow>
                    <span style={{ width: '100%' }}>
                      Cliente: {selectedCustomer?.customerName || 'Cliente General'}
                    </span>
                  </ReceiptRow>
                  {selectedCustomer?.identityDocumentNumber ? (
                    <ReceiptRow>
                      <span style={{ width: '100%' }}>
                        CI: {selectedCustomer.identityDocumentNumber}
                      </span>
                    </ReceiptRow>
                  ) : null}
                  {selectedCustomer?.customerEmail ? (
                    <ReceiptRow>
                      <span style={{ width: '100%' }}>
                        Correo: {selectedCustomer.customerEmail}
                      </span>
                    </ReceiptRow>
                  ) : null}
                </ReceiptSection>

                <ReceiptDivider>
                  <ReceiptBold>DETALLE DE PRODUCTOS</ReceiptBold>
                </ReceiptDivider>

                <ReceiptSection>
                  {selectedProducts.map((product: SelectedProduct, index: number) => (
                    <ReceiptProductRow key={index}>
                      <ReceiptRow>
                        <ReceiptBold style={{ width: '100%' }}>
                          {product.warehouseInventoryProduct.product.name}
                        </ReceiptBold>
                      </ReceiptRow>
                      <ReceiptRow>
                        <span>1 x {formatCurrency(product.originalPrice)}</span>
                        <span style={{ textAlign: 'right' }}>
                          {formatCurrency(product.originalPrice)}
                        </span>
                      </ReceiptRow>
                    </ReceiptProductRow>
                  ))}
                </ReceiptSection>

                <ReceiptDivider>
                  <ReceiptBold>RESUMEN</ReceiptBold>
                </ReceiptDivider>

                <ReceiptSection>
                  <ReceiptTotal>
                    <span>TOTAL:</span>
                    <span style={{ textAlign: 'right' }}>{formattedGrandTotal}</span>
                  </ReceiptTotal>
                </ReceiptSection>

                <ReceiptDivider>
                  <div style={{ textAlign: 'center', fontSize: '0.875rem' }}>
                    <div style={{ marginBottom: '8px' }}>¡Gracias por su compra!</div>
                    <div>{STORE_INFO.website}</div>
                  </div>
                </ReceiptDivider>
              </Receipt>
            </PrintModalBody>

            {/* Print Modal Buttons */}
            <PrintModalFooter>
              <PrimaryButton onClick={handlePrint} style={{ flex: 1 }}>
                <PrintIcon style={{ fontSize: 16, marginRight: 8 }} />
                Imprimir
              </PrimaryButton>
              <SecondaryButton onClick={handleClosePrintModal} style={{ flex: 1 }}>
                Cerrar
              </SecondaryButton>
            </PrintModalFooter>
          </PrintModalContent>
        </PrintModalBackdrop>
      )}

      {/* Close Confirmation Modal */}
      <CloseConfirmationModal
        open={showCancelConfirmation}
        onClose={handleCloseConfirmation}
        onConfirm={cancelOperation}
      />
    </LocalizationProvider>
  );
};

export default SalesModal;
